import { ChangeEvent, useCallback } from 'react'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { ReactGeocodePlace } from 'react-geocode'
import { useTranslation } from 'react-i18next'

import { AddressSearchInput } from 'components/common/AddressSearchInput'
import { logger } from 'utils/logging'
import { lookupPlaceFromAddress } from 'utils/helpers/lookUpPlaceFromAddress'

import { useAddressMap, zoomedIn } from '../AddressMap.context'

export interface Props {
  /** error text to display under the input */
  error?: string
  /** Action called when address changes */
  handleChange: (place: ReactGeocodePlace | null, isInitialLookup: boolean) => void
  /** Handle autocomplete input change */
  onInputChange?: (event: ChangeEvent<HTMLInputElement>) => void
  /** Optional hook for autocomplete input blur event */
  onInputBlur?: () => void
  /** A string or an array of countries such es ['CH, AT, DE'] to restrict the search to */
  restrictToCountries?: string | string[]
  /** Whether the input is disabled */
  disabled?: boolean
}

/** A picked suggestion in the shape the map and its consumers read, or null when it has no position. */
const toGeocodePlace = (place: google.maps.places.PlaceResult): ReactGeocodePlace | null => {
  if (!place.geometry?.location || !place.formatted_address) return null

  return {
    ...place,
    geometry: { location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() } },
    place_id: place.place_id ?? '',
    formatted_address: place.formatted_address,
    address_components: place.address_components ?? [],
  } as ReactGeocodePlace
}

/**
 * The address field of the address map: Google address suggestions, and an address typed in full
 * and confirmed with Enter is looked up instead.
 *
 * @example
 * <AutocompleteInput handleChange={(place) => setPlace(place)} />
 */
export const AutocompleteInput: React.FC<Props> = ({
  error,
  handleChange,
  onInputChange,
  onInputBlur,
  restrictToCountries,
  disabled = false,
}) => {

  const { t } = useTranslation('address_map')

  const {
    address,
    setAddress,
    setZoom,
    updateStateFromPlace,
  } = useAddressMap()

  const applyPlace = useCallback((place: ReactGeocodePlace | null) => {
    setZoom(zoomedIn)
    updateStateFromPlace(place)
    handleChange(place, false)
  }, [handleChange, setZoom, updateStateFromPlace])

  const lookUpTypedAddress = useCallback(async (text: string) => {
    if (!text.trim()) return
    try {
      applyPlace(await lookupPlaceFromAddress(text))
    } catch {
      logger.warn('Address map lookup of a typed address failed', {
        context: { component: 'AutocompleteInput', action: 'lookUpTypedAddress' },
      })
      applyPlace(null)
    }
  }, [applyPlace])

  return (
    <AddressSearchInput
      value={disabled ? '' : address}
      disabled={disabled}
      placeholder={t('placeholder')}
      startIcon={<SearchOutlinedIcon sx={{ fontSize: '1.6rem' }} />}
      isError={!!error}
      errorText={error}
      restrictToCountry={restrictToCountries}
      onChange={(e) => {
        setAddress(e.target.value)
        onInputChange?.(e)
      }}
      onBlur={onInputBlur}
      onSelect={(_, place) => applyPlace(toGeocodePlace(place))}
      onSubmitText={lookUpTypedAddress}
    />
  )
}
